import { createSlice } from "@reduxjs/toolkit";
import { FilterType, StatusFilterType } from "@/utils/utils";
import { REHYDRATE } from "redux-persist"

export const statusTypes = {
  Error: 0,
  NotQueued: 1,
  Processing: 2,
  Successful: 3,
  PendingAck: 4,
  CompletedWithWarning: 5,
};

export const statusLabels = {
  [statusTypes.Error]: "Error",
  [statusTypes.NotQueued]: "Not Queued",
  [statusTypes.Processing]: "Processing",
  [statusTypes.PendingAck]: "Pending Acknowledgement",
  [statusTypes.Successful]: "Completed",
  [statusTypes.CompletedWithWarning]: "Completed With Warning",
}

const workflowManagerSessionSlice = createSlice({
  name: "workflowManagerSession",
  initialState: {
    selectedWorkflow: 0,
    paginationModel: { pageSize: 15, page: 0 },
    managerSearchString: "",
    searchAll: false, //flag used to ignore date filters. Reset when search is changed / cleared
    filters: {
      status: [],
      type: [],
      applyFilters: false,
    },
  },
  reducers: {
    setSelectedWorkflow: (state, action) => {
      const { selectedWorkflow } = action.payload;
      state.selectedWorkflow = selectedWorkflow;

      //reset to default values when changing workflow lists
      workflowManagerSessionSlice.caseReducers.resetFilters(state, action);
      state.paginationModel.page = 0;
    },
    setSessionPagination: (state, action) => {
      const { newPaginationModel } = action.payload;
      state.paginationModel = newPaginationModel;
    },
    setManagerSearchString: (state, action) => {
      const { searchString, searchAll = false } = action.payload;
      state.managerSearchString = searchString;
      state.searchAll = searchAll;
    },
    setFilterType: (state, action) => {
      const { value, applyFilter = true } = action.payload;
      state.filters.applyFilters = applyFilter;
      state.filters.type = value;

      // heap.io: Only track if change is due to user input
      if (action.payload.applyFilter !== false) {
        heapIOTrackFilter("Type", action.payload.value);
      }
    },
    setFilterStatus: (state, action) => {
      const { value, applyFilter = true } = action.payload;
      state.filters.applyFilters = applyFilter;
      state.filters.status = value;
      // heap.io: Only track if change is due to user input
      if (action.payload.applyFilter !== false) {
        heapIOTrackFilter("Status", action.payload.value);
      }
    },

    resetFilters: (state, action) => {
      // SHEP-141: Reset filters when operation changes
      const status = [
        {
          id: statusTypes.Successful,
          label: statusLabels[statusTypes.Successful],
          value: StatusFilterType.Successful,
          isChecked: false,
        },
        {
          id: statusTypes.CompletedWithWarning,
          label: statusLabels[statusTypes.CompletedWithWarning],
          value: StatusFilterType.CompletedWithWarning,
          isChecked: false,
        },
        {
          id: statusTypes.Error,
          label: statusLabels[statusTypes.Error],
          value: StatusFilterType.Error,
          isChecked: false,
        },
        {
          id: statusTypes.PendingAck,
          label: statusLabels[statusTypes.PendingAck],
          value: StatusFilterType.PendingAck,
          isChecked: false,
        },
        {
          id: statusTypes.Processing,
          label: statusLabels[statusTypes.Processing],
          value: StatusFilterType.Processing,
          isChecked: false,
        },
        {
          id: statusTypes.NotQueued,
          label: statusLabels[statusTypes.NotQueued],
          value: StatusFilterType.NotQueued,
          isChecked: false,
        },
      ];

      const type = [
        {
          id: 0,
          label: "Archived",
          value: FilterType.Archived,
          isChecked: false,
        },
        {
          id: 1,
          label: "On Hold",
          value: FilterType.OnHold,
          isChecked: false,
        },
        {
          id: 2,
          label: "Active",
          value: FilterType.Active,
          isChecked: false,
        },
        {
          id: 3,
          label: "Unassigned",
          value: FilterType.Unassigned,
          isChecked: false,
        },
      ];

      state.filters.type = type;
      state.filters.status = status;
      state.filters.applyFilters = false;
    }
  },

  extraReducers: (builder) => {
    //The unassigned filter should not persist on refresh
    builder.addCase(REHYDRATE, (state, action) => {
      if (action.payload && action.payload.workflowManagerSession) {
        return {
          ...action.payload.workflowManagerSession,
          filters: {
            ...action.payload.workflowManagerSession.filters,
            type:
              [
                ...action.payload.workflowManagerSession.filters.type.filter(type => type.value !== FilterType.Unassigned),
                {
                  id: 3,
                  label: "Unassigned",
                  value: FilterType.Unassigned,
                  isChecked: false,
                },
              ]
          }
        }
      }
    })
  }
});

export function heapIOTrackFilter(filterType, filterState) {
  const eventProps = {};

  const selectedItems = filterState
    .filter((item) => item.isChecked)
    .map((item) => item.label);

  let filterText;

  if (selectedItems.length === filterState.length) {
    filterText = "All";
  } else if (selectedItems.length === 0) {
    filterText = "None";
  } else {
    filterText = selectedItems.join(",");
  }

  eventProps[`Filter - ${filterType}`] = filterText;

  window.heap.track("Workflow Manager - Update Filter", eventProps);
}

export const selectWorkflow = (state) =>
  state.sessionInfo.workflowManagerSession.selectedWorkflow;
export const selectManagerSearchString = (state) =>
  state.sessionInfo.workflowManagerSession.managerSearchString;
export const selectFilterType = (state) =>
  state.sessionInfo.workflowManagerSession.filters.type;
export const selectFilterStatus = (state) =>
  state.sessionInfo.workflowManagerSession.filters.status;
export const selectFilters = (state) =>
  state.sessionInfo.workflowManagerSession.filters;
export const selectSessionPagination = (state) =>
  state.sessionInfo.workflowManagerSession.paginationModel;

export const {
  setManagerSearchString,
  setFilterType,
  setFilterStatus,
  setSelectedWorkflow,
  setSessionPagination,
} = workflowManagerSessionSlice.actions;

export default workflowManagerSessionSlice.reducer;
